import { PAGE_NAME_NAVIGATION } from '@/constants/pages'
import { ABERRANT_TYPES } from '@/constants/aberrantCodes'
import { type Plans } from '@/composables/allSignatureChangePlan'
import { PageSection } from '@/constants/bookings'
import {
  type PhoneNumberBlockTypes,
  type PhoneNumberBlockTypesWithoutNew
} from '@/constants/security'
import { STATUS_RULE } from './loyalty'
import {
  type SouscriptionAndPlanLabel,
  SOUSCRIPTION_AND_PLAN_LABELS_GTM,
  SUBSCRIPTION_NAME
} from '@/constants/subscriptions'
import type { StatusGiftType } from './loyaltyGiftStatus'

const ACCOUNT_CONTEXT = 'account'

/** SIDE NAVIGATION  **/

export const GTM_MENU_ITEM = (name: string) => ({
  event: 'GA4event',
  eventName: 'menu_interact',
  event_data: {
    pagename: PAGE_NAME_NAVIGATION[name],
    menu_position: Object.keys(PAGE_NAME_NAVIGATION).indexOf(name) + 1,
    menu_label: name
  }
})

/** GLOBAL TRANSACTION HISTORY PAGE **/

export const GTM_CREDIT_LEARN_MORE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_type: 'hero banner',
    bloc_interaction: 'learn more',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_MORE_INFO_POINTS_CTA_DISPLAY_MODAL = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_name: 'points credit',
    bloc_interaction: 'more information',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_CLAIM_YOUR_POINTS_CTA = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_name: 'points credit',
    bloc_interaction: 'claim your points',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SLIDER_CLAIM_YOUR_POINTS_CTA = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_name: 'points credit',
    bloc_interaction: 'slider claim your points',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SEARCH_POINTS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_name: 'points credit',
    bloc_interaction: 'search',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_USE_CASE_CLAIM_POINTS = ({
  blockName,
  blockInteraction,
  blockType
}: Record<string, string>) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_name: blockName,
    bloc_interaction: blockInteraction,
    bloc_type: blockType,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_CHOOSE_CLAIM_POINTS_STAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_name: 'points credit',
    bloc_interaction: 'past stay',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_CHOOSE_CLAIM_POINTS_OTHER = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_name: 'points credit',
    bloc_interaction: 'another activity',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_HOW_TO_EARN_POINTS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_type: 'hero banner',
    bloc_interaction: 'how to earn points',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_FIND_A_HOTEL = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_type: 'hero banner',
    bloc_interaction: 'search a hotel',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_HOW_TO_USE_POINTS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::auto retroclaim',
    bloc_type: 'hero banner',
    bloc_interaction: 'how to use points',
    context: ACCOUNT_CONTEXT
  }
})

/** MY LOYALTY PROGRAM PAGE **/

export const GTM_REWARDS_POINTS_LEARN_MORE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your rewards',
    bloc_interaction: 'learn more',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_EARN_POINTS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your rewards',
    bloc_interaction: 'earn points',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_HOW_IT_WORK = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your status',
    bloc_interaction: 'how its works',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_RECEIVE_MY_CARD = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your status',
    bloc_interaction: 'receive my card',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_NEED_HELP = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your status',
    bloc_interaction: 'need help',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SNU_LEARN_MORE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your rewards - snu',
    bloc_interaction: 'learn more',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_DIN_LEARN_MORE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your rewards - din',
    bloc_interaction: 'learn more',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_FAIRMONT_LEARN_MORE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your rewards - fairmont',
    bloc_interaction: 'learn more',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_HOW_TO_USE_SLIDER_REWARDS = ({ award }: { award: 'snu' | 'din' }) => {
  const blockType = ['slider']

  switch (award) {
    case 'snu':
      blockType.push('cta_snu')
      break
    case 'din':
      blockType.push('cta_din')
      break
    default:
      blockType.push('cta_reward points')
  }

  return {
    event: 'GA4event',
    eventName: 'bloc_interact',
    event_data: {
      pagename: 'account::loyalty',
      bloc_type: blockType.join(' '),
      bloc_interaction: 'how to use',
      context: ACCOUNT_CONTEXT
    }
  }
}

export const GTM_BENEFIT_LEARN_MORE = (benefit: Record<string, string>) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: `your major benefits - ${benefit.selectedTab} status`,
    bloc_interaction: `learn more - ${benefit.name}`,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SEE_ALL_BENEFITS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your major benefits',
    bloc_interaction: 'see all benefits',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SEE_MORE_DETAILS_PARTNER = (partnerName: string) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'more points more quickly',
    bloc_interaction: `see more details - ${partnerName}`,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_DISCOVER_OFFER = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'more points more quickly',
    bloc_interaction: 'discover offer',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_DISCOVER_OTHER_OFFERS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'more points more quickly',
    bloc_interaction: 'discover other offers',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_LIMITLESS_ASSISTANCE_CONDITIONS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your status - limitless',
    bloc_interaction: 'see conditions',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_LIMITLESS_ASSISTANCE_GET_CARD = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your status - limitless',
    bloc_interaction: 'receive your card',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_HOW_TO_USE_AWARDS = (data: Record<string, string>) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: `slider cta_${data.award}`,
    bloc_interaction: 'how to use',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_MEMBER_GIFT_LEARN_MORE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_type: 'your rewards',
    bloc_interaction: 'member gift',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_NOT_ELIGIBLE_TRANSACTION = (aberrantCode: keyof typeof ABERRANT_TYPES) => {
  const aberrantMap = {
    [ABERRANT_TYPES.ABERRANT_OLTA_RESERVATION]: 'not eligible plateform',
    [ABERRANT_TYPES.ABERRANT_INELIGIBLE_RATE_CODE]: 'not eligible rate',
    [ABERRANT_TYPES.ABERRANT_PMSEXTRACTOR_CHANNEL_CODE_EXCLUSION]:
      'pmsextractor channel code exclusion',
    [ABERRANT_TYPES.ABERRANT_OVERLAP_SAME_HOTEL]: 'overlap same hotel',
    [ABERRANT_TYPES.ABERRANT_OVERLAP_DIFFERENT_HOTEL]: 'overlap different hotel',
    [ABERRANT_TYPES.ABERRANT_HOTEL_ACCOUNT_LOCKED]: 'hotel account locked',
    [ABERRANT_TYPES.ABERRANT_HOTEL_NO_CARD_AT_DECLARATION_DATE]:
      'hotel no card at declaration date',
    [ABERRANT_TYPES.UNKNOWN_PMID]: 'unknown pmid',
    [ABERRANT_TYPES.ABERRANT_HOTEL_INVALID_CARD_NUMBER]: 'hotel invalid card number',
    [ABERRANT_TYPES.ABERRANT_HOTEL_INVALID_LUHN_KEY]: 'hotel invalid luhn key',
    [ABERRANT_TYPES.ABERRANT_UNKNOWN_PLACE]: 'unknown place',
    [ABERRANT_TYPES.ABERRANT_STAY_DATE_BEFORE_OPENING_DATE]: 'stay date before opening date',
    [ABERRANT_TYPES.ABERRANT_NUMBER_OF_NIGHTS_TO_BIG]: 'number of nights to big',
    [ABERRANT_TYPES.ABERRANT_NUMBER_OF_NIGHTS_TO_SMALL]: 'number of nights to small',
    [ABERRANT_TYPES.ABERRANT_INCONSISTENT_DATES]: 'inconsistent dates',
    [ABERRANT_TYPES.ABERRANT_INVALID_DATES_ORDER]: 'invalid dates order',
    [ABERRANT_TYPES.ABERRANT_INVALID_CHECKIN_DATE]: 'invalid checkin date',
    [ABERRANT_TYPES.ABERRANT_INVALID_CHECKOUT_DATE]: 'invalid checkout date',
    [ABERRANT_TYPES.ABERRANT_PMSEXTRACTOR_NO_STAY_EXPENSE]: 'pmsextractor no stay expense',
    [ABERRANT_TYPES.ABERRANT_HOTEL_UNKNOWN_PRICE]: 'hotel unknown price',
    [ABERRANT_TYPES.ABERRANT_INVALID_PRICE_RANGE]: 'invalid price range',
    [ABERRANT_TYPES.ABERRANT_INVALID_PRICE_RANGE_DAY_USE]: 'invalid price range day use',
    [ABERRANT_TYPES.ABERRANT_BUSINESS_LOCKED_COMPANY]: 'business locked company',
    [ABERRANT_TYPES.ABERRANT_INELIGIBLE_SEGMENTATION]: 'not eligible segmentation'
  }

  return {
    event: 'GA4event',
    eventName: 'bloc_interact',
    event_data: {
      pagename: 'account::activity',
      bloc_type: 'content display',
      bloc_interaction: aberrantMap[aberrantCode],
      context: ACCOUNT_CONTEXT
    }
  }
}
export const GTM_TSM_HOW_IT_WORK = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::activity',
    bloc_type: 'temporary status match',
    bloc_interaction: 'how it works',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ECONOMY_DISPLAYED = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_name: 'display',
    bloc_type: 'savings calculator',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ECONOMY_CTA = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_name: 'click on cta',
    bloc_interaction: 'see savings details',
    bloc_type: 'savings calculator',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_DISPLAY_POPIN_ALERT_STATUS = (statusRule: STATUS_RULE) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_name: `status ${statusRule === STATUS_RULE.KEEPING ? 'keeping' : 'progression'}`,
    bloc_interaction: 'popin',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_POPIN_ALERT_STATUS_CTA = (statusRule: STATUS_RULE) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::loyalty',
    bloc_name: `status ${statusRule === STATUS_RULE.KEEPING ? 'keeping' : 'progression'}`,
    bloc_interaction: 'learn more',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

/*  --------------- MY BOOKING  --------------- */
const BOOKINGS_PAGENAME = 'account::bookings'

const bookingsEvent = (blockType: string) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_type: blockType,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SEARCH_HOTEL = () => bookingsEvent('search an hotel')

export const GTM_SEE_OLD_RESERVATIONS = () => bookingsEvent('see my old reservations')

export const GTM_BOOK_AGAIN = () => bookingsEvent('book again')

export const GTM_REQUEST_INVOICE = () => bookingsEvent('ask your invoice')

export const GTM_DOWNLOAD_INVOICE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_interaction: 'download your invoice',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SEE_MORE_BOOKINGS = () => bookingsEvent('see more')

export const GTM_SEE_DETAILS_BOOKINGS = () => bookingsEvent('see details')

export const GTM_ONLINE_CHECK_IN = () => bookingsEvent('online check in')

export const GTM_HOTEL_FACTSHEET_NAME = () => bookingsEvent('hotel factsheet name')

const ADD_BOOKING_PAGENAME = 'account::add a booking'
const ADD_BOOKING_FUNCTIONAL_ERROR = 'add a booking - functional error'
const ADD_BOOKING_TECHNICAL_ERROR = 'add a booking - technical error'

export const GTM_ADD_BOOKING = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'add a booking',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_SEARCH = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'search',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_MUST_BE_FUTURE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_type: 'error',
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'booking must be in future',
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_TECHNICAL_ERROR = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_type: 'error',
    pagename: ADD_BOOKING_PAGENAME,
    bloc_name: ADD_BOOKING_TECHNICAL_ERROR,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_NOT_FOUND = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_type: 'error',
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'booking not found',
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_NUMBER_ERROR = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: ADD_BOOKING_PAGENAME,
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    bloc_interaction: 'booking number',
    bloc_type: 'error',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_INACTIVE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_type: 'error',
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'booking must be active',
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_ON_ANOTHER_ACCOUNT = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_type: 'error',
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'booking on other account',
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_LAST_NAME_MISMATCH = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_type: 'error',
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'booking customer lastname no match',
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_ALREADY_ADDED = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_type: 'error',
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'booking customer already on the account',
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_SUCCESS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'success',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_SAME_ACCOUNT = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: ADD_BOOKING_PAGENAME,
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    bloc_interaction: 'on same account',
    bloc_type: 'error',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_ADD_BOOKING_ABERRANT_MULTI_ORDERS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: ADD_BOOKING_PAGENAME,
    bloc_name: ADD_BOOKING_FUNCTIONAL_ERROR,
    bloc_interaction: 'several stays',
    bloc_type: 'error',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_TRIP_EUROPCAR_CLICK = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_interaction: 'see details mobility',
    context: ACCOUNT_CONTEXT
  }
})

const bookingsTabsInteraction = {
  [PageSection.Hotel]: 'stays',
  [PageSection.Mobility]: 'trips',
  [PageSection.Experience]: 'activities'
}

export const GTM_ID_MY_BOOKINGS_TAB_CTA = (mobilityType: string) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_interaction: bookingsTabsInteraction[mobilityType],
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_SEE_MORE = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_name: 'rewards and status points',
    pagename: BOOKINGS_PAGENAME,
    bloc_interaction: 'see more',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_GO_TO_POINTS_STATEMENTS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_interaction: 'see in my points statement',
    context: ACCOUNT_CONTEXT,
    bloc_type: 'click on cta'
  }
})

/*  --------------- MY PARTNERS  --------------- */
const PARTNERS_BENEFITS_PAGENAME = 'account::your partner benefits'

export const GTM_PARTNERS_BENEFITS = (partner) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_name: 'partners link to your account',
    bloc_interaction: `see my benefits ${partner}`,
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PARTNERS_UNLINK = (partner) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_name: 'partners link to your account',
    bloc_interaction: `dissociate ${partner}`,
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PARTNERS_SEE_ALL = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_interaction: 'see all your partners',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PARTNERS_BENEFITS_SEE_MORE = (partner) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_name: partner,
    bloc_interaction: 'learn more',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PARTNERS_DISSOCIATE_CONFIRM = (partner) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_name: partner,
    bloc_interaction: 'dissociate confirmation',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PARTNERS_DISSOCIATE_CANCEL = (partner) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_name: partner,
    bloc_interaction: 'delete dissociation',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PARTNERS_DISSOCIATE_SUCCESS = (partner) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_name: partner,
    bloc_interaction: 'success dissociation',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PARTNERS_DISSOCIATE_ERROR = (partner) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: PARTNERS_BENEFITS_PAGENAME,
    bloc_name: partner,
    bloc_interaction: 'error dissociation',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

/*  --------------- ALL SIGNATURE CHANGE PLAN  --------------- */

const ALL_SIGNATURE_HOME = 'account::all signature'
const ALL_SIGNATURE_CHANGE_PLAN_PAGENAME = 'account::all signature::plan modification'
const ALL_SIGNATURE_CHANGE_PLAN_POPIN_PAGENAME =
  'account::all signature::popin plan modification validation'

export const GTM_ALL_SIGNATURE_CLICK_ON_CHANGE_PLAN = ({
  planName,
  frequency
}: {
  planName: string
  frequency: string
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_name: 'change plan',
    pagename: ALL_SIGNATURE_CHANGE_PLAN_PAGENAME,
    bloc_interaction: `${frequency} ${planName}`
  }
})

export const GTM_ALL_SIGNATURE_CHANGE_PLAN_MODAL_CONFIRM = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_name: 'popin changing plan validation',
    pagename: ALL_SIGNATURE_CHANGE_PLAN_POPIN_PAGENAME,
    bloc_interaction: 'confirmation'
  }
})

export const GTM_ALL_SIGNATURE_CHANGE_PLAN_MODAL_CANCEL = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_name: 'popin changing plan validation',
    pagename: ALL_SIGNATURE_CHANGE_PLAN_POPIN_PAGENAME,
    bloc_interaction: 'cancel'
  }
})

export const GTM_ALL_SIGNATURE_CHANGE_PLAN_ERROR = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_name: 'popin changing plan',
    pagename: ALL_SIGNATURE_CHANGE_PLAN_POPIN_PAGENAME,
    bloc_interaction: 'error'
  }
})

export const GTM_ALL_SIGNATURE_CHANGE_PLAN_SUCCESS = (planName: Plans) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    bloc_name: 'banner changing plan',
    pagename: `${ALL_SIGNATURE_HOME}::${planName}`,
    bloc_interaction: 'success'
  }
})

export const GTM_RESERVATION_NUMBER_INFO = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: ADD_BOOKING_PAGENAME,
    bloc_interaction: 'your reservation number',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_EUROP_CAR_DISPLAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'europcar',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_EUROP_CAR_CTA = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'europcar',
    bloc_interaction: 'see offers',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_ESIM_DISPLAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'orange travel',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_ESIM_CTA = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'orange travel',
    bloc_interaction: 'see the offers',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_DOWNLOAD_APP_DISPLAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'app all.com',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_WELCOME_DRINK_DISPLAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'welcome drink',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_USE_DIN_DISPLAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'dinning and spa rewards',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_USE_DIN_CTA = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'dinning and spa rewards',
    bloc_interaction: 'more information',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_FEVER_SEE_ALL = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: 'fever',
    bloc_interaction: 'see all activities',
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_FEVER_DISPLAY = (name) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: name,
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_BOOKING_PUSH_FEVER_CTA = (name) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: BOOKINGS_PAGENAME,
    bloc_name: name,
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

/** My Information  */

const MY_INFO_PAGENAME = 'account::my information'

export const INFO_FIELD_ACTIONS = {
  ADD: 'add',
  MODIFY: 'modify',
  DELETE: 'delete',
  SAVE: 'save',
  CUSTOMER: 'contact customer service'
} as const

type InfosFieldActionKey = keyof typeof INFO_FIELD_ACTIONS
export type InfoFieldAction = (typeof INFO_FIELD_ACTIONS)[InfosFieldActionKey]

export const INFO_TYPE = {
  PERSONAL: 'Personal',
  PROFESSIONAL: 'Professional'
} as const

type InfosTypeKey = keyof typeof INFO_TYPE
export type InfoType = (typeof INFO_TYPE)[InfosTypeKey]

export const INFO_FIELDS = {
  NAME: 'first and last name',
  BILLING_ADDRESS: 'billing address',
  ADDRESS: 'address',
  BIRTH_DATE: 'date of birth',
  BIRTH_PLACE: 'place of birth',
  CIVILITY: 'civility',
  EMAIL: 'email address',
  NATIONALITY: 'nationality',
  PHONE: 'phone number',
  COMPANY_IDENTIFIER: 'accor SC/AC company identifier access code',
  VAT: 'vat number',
  IDENTITY: 'identity',
  PASSWORD: 'password'
} as const

type InfosFieldsKey = keyof typeof INFO_FIELDS
export type InfoFields = (typeof INFO_FIELDS)[InfosFieldsKey]

export const getInfoFieldInformationAnalyticsProp = ({
  name,
  contextType
}: {
  name: InfoFields
  contextType: InfoType
}) => ({
  name,
  pageName: MY_INFO_PAGENAME,
  blockType: contextType.toLocaleLowerCase() + ' information'
})

export const GTM_INFORMATION_CTA = ({
  name,
  pageName,
  infoFieldAction,
  blockType
}: {
  name: InfoFields
  pageName: string
  infoFieldAction: InfoFieldAction
  blockType: string
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    bloc_name: name,
    bloc_interaction: infoFieldAction,
    bloc_type: blockType,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_INFORMATION_CUSTOMER_SERVICE = () =>
  GTM_INFORMATION_CTA({
    name: INFO_FIELDS.IDENTITY,
    pageName: MY_INFO_PAGENAME,
    infoFieldAction: INFO_FIELD_ACTIONS.CUSTOMER,
    blockType: INFO_TYPE.PERSONAL.toLocaleLowerCase() + ' information'
  })

export const GTM_INFORMATION_SELF_MERGE_DISPLAY = ({ contextType }: { contextType: InfoType }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: MY_INFO_PAGENAME,
    bloc_name: 'email merge contact',
    bloc_interaction: 'display',
    bloc_type: contextType.toLocaleLowerCase() + ' information',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_INFORMATION_SELF_MERGE_ADD = ({ contextType }: { contextType: InfoType }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: MY_INFO_PAGENAME,
    bloc_name: 'email merge contact',
    bloc_interaction: 'add email',
    bloc_type: contextType.toLocaleLowerCase() + ' information',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_INFORMATION_SELF_MERGE_NOT_ADD = (contextType: InfoType) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: MY_INFO_PAGENAME,
    bloc_name: 'email merge contact',
    bloc_interaction: 'do not want add email',
    bloc_type: contextType.toLocaleLowerCase() + ' information',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_INFORMATION_MODIFY_NUMBER_EVENTS = ({
  interaction,
  contextType
}: {
  interaction: string
  contextType: InfoType
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: MY_INFO_PAGENAME,
    bloc_name: 'two steps sms validation',
    bloc_interaction: interaction,
    bloc_type: `${contextType.toLowerCase()} information`,
    context: ACCOUNT_CONTEXT
  }
})

/** Preferences  */
export const GTM_PREFERENCE_QUESTION_START = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'preferences introduction',
    bloc_interaction: 'tell us more',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PREFERENCE_QUESTION_END = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'preferences validation',
    bloc_interaction: 'success',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PREFERENCE_QUESTION_ERROR = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'error form preferences',
    bloc_interaction: 'technical error',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PREFERENCE_QUESTION_STEP = ({
  name,
  interaction
}: {
  name: string
  interaction: string
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: name,
    bloc_interaction: interaction,
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

const preferencesFamilies: Record<string, string> = {
  'F&B': 'food and beverage',
  Room: 'room',
  Interests: 'interests'
}

export const GTM_ADD_PREFERENCE = ({
  prefCode,
  prefCategory
}: {
  prefCode: string
  prefCategory: string
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'add stay preferences',
    bloc_interaction: prefCode,
    bloc_type: preferencesFamilies[prefCategory],
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_DELETE_PREFERENCE = ({
  prefCode,
  prefCategory
}: {
  prefCode: string
  prefCategory: string
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'delete stay preferences',
    bloc_interaction: prefCode,
    bloc_type: preferencesFamilies[prefCategory],
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_DISPLAY_SENSITIVE_PREFERENCE_MODAL = (prefCode: string) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'sensitive value',
    bloc_interaction: prefCode,
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SENSITIVE_PREFERENCE_MODAL_CTA = (interaction: 'cancel' | 'validate') => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'sensitive value',
    bloc_interaction: interaction,
    bloc_type: 'click on cta',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_PAGINATED_PREFERENCE_RECOMMENDATIONS = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::stay preferences',
    bloc_name: 'stay preferences',
    bloc_interaction: 'see other choice',
    bloc_type: 'bloc suggestions',
    context: ACCOUNT_CONTEXT
  }
})

/** Security settings  */

export const SECURITY_SETTINGS_PAGENAME = 'account::security settings'

export const GTM_PAGE_SECURITY_SETTINGS = () => ({
  category: 'account',
  pageName: SECURITY_SETTINGS_PAGENAME,
  sub_category_level1: 'security settings'
})

export const getInfoFieldSecurityAnalyticsProp = () => ({
  name: 'security phone' as InfoFields,
  pageName: SECURITY_SETTINGS_PAGENAME,
  blockType: 'security settings'
})

export const GTM_SECURITY_SETTINGS_SECURE_MY_ACCOUNT = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'two steps sms validation',
    bloc_interaction: 'secure my account',
    bloc_type: 'security settings',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_ADD_ANOTHER_NUMBER = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider two steps sms validation',
    bloc_interaction: 'add another number',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_ADD_NEW_NUMBER = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider two steps sms validation',
    bloc_interaction: 'add new number',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_CHOOSE_A_NUMBER = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider two steps sms validation',
    bloc_interaction: 'choose a number',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_MODIFY_NUMBER = (
  blockType: PhoneNumberBlockTypesWithoutNew
) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider two steps sms validation',
    bloc_interaction: 'modify number',
    bloc_type: blockType,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_VERIFICATION_CODE = (blockType: PhoneNumberBlockTypes) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider two steps sms validation',
    bloc_interaction: 'verification code',
    bloc_type: blockType,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_NUMBER_VALIDATION = (blockType: PhoneNumberBlockTypes) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider two steps sms validation',
    bloc_interaction: 'number validation',
    bloc_type: blockType,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_RECEIVE_CODE = (blockType: PhoneNumberBlockTypes) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider two steps sms validation',
    bloc_interaction: 'receive a new code',
    bloc_type: blockType,
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_PRIVACY_CTA = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'security settings',
    bloc_interaction: 'security activated',
    bloc_type: 'privacy and security',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_PRIVACY_ACTIVATED_DISPLAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'security settings',
    bloc_interaction: 'security activated',
    bloc_type: 'security settings',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_MFA_TOGGLE_CTA = (action: 'activate' | 'deactivate') => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'two steps sms validation',
    bloc_interaction: action === 'activate' ? 'activate' : 'deactivate number',
    bloc_type: 'security settings',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_MFA_DISABLE_DISPLAY = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider disable two steps sms activation',
    bloc_interaction: 'disable authentification',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_MFA_DISABLE_CONFIRM = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'slider disable two steps sms activation',
    bloc_interaction: 'disable authentification',
    bloc_type: 'validate deactivation',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SECURITY_SETTINGS_PWD_ERROR = (
  action: 'same password' | 'invalid password' | 'failed password'
) => ({
  event: 'GA4event',
  eventName: 'form_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    form_action: 'error',
    error_type: 'technical error',
    error_field: action
  }
})

export const GTM_SECURITY_SETTINGS_MODIFY_NUMBER_EVENTS = (interaction: string) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: SECURITY_SETTINGS_PAGENAME,
    bloc_name: 'security phone',
    bloc_interaction: interaction,
    bloc_type: 'security settings',
    context: ACCOUNT_CONTEXT
  }
})

/** Otp errors  */

export const GTM_OTP_ERROR_RECEIVE_NEW_CODE = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'receive new code'
  }
})

export const GTM_OTP_ERROR_INCORRECT_CODE = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'incorrect code'
  }
})

export const GTM_OTP_ERROR_CODE_NOT_IDENTIFIED = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'code not identified'
  }
})

export const GTM_TECHNICAL_ERROR_PING = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'phone number already used'
  }
})

export const GTM_OTP_ERROR_INVALID_CODE_THREE_TIMES = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'three time wrong code'
  }
})

export const GTM_OTP_ERROR_MAX_DAILY_REQUEST_REACHED = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'retry in 24h'
  }
})

export const GTM_MODIFICATION_ALL_SIGNATURE_PLAN = (newPlanName: string) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${newPlanName}`,
    bloc_name: newPlanName,
    bloc_interaction: 'modification plan',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_OPEN_SUBSCRIPTION_CARD_PAGE = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: 'account::hub subscription',
    bloc_name: planLabel,
    bloc_interaction: 'see subscription card',
    bloc_type: 'subscription program',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_PREMIUM_CUSTOMER_SERVICE = ({
  planLabel
}: {
  planLabel: string
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'see subscription card',
    bloc_type: 'subscription program',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_SEE_ALL_BENEFITS = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'see all benefits',
    bloc_type: 'benefits',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_END_CTA = ({
  planLabel,
  action
}: {
  planLabel: string
  action: 'see other offers' | 'confirm cancellation'
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: action,
    bloc_type: 'subscription end',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_END_POPIN = ({ planLabel }: { planLabel: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'subscription end',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_GO_TO_APP = () => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${SOUSCRIPTION_AND_PLAN_LABELS_GTM[SUBSCRIPTION_NAME.ALLPLUS_EXPLORER]}`,
    bloc_name: SOUSCRIPTION_AND_PLAN_LABELS_GTM[SUBSCRIPTION_NAME.ALLPLUS_EXPLORER],
    bloc_interaction: 'go to the app',
    bloc_type: 'vouchers and discount',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_VIEWS_DETAILS_VOUTCHER = ({ category }: { category: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${SOUSCRIPTION_AND_PLAN_LABELS_GTM[SUBSCRIPTION_NAME.ALLPLUS_EXPLORER]}`,
    bloc_name: category,
    bloc_interaction: 'views details',
    bloc_type: 'vouchers and discount',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_REACTIVATE_CTA = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'reactivate my subscription',
    bloc_type: 'your rewards',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_RENEW_SUBSCRIPTION_CTA = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'renew',
    bloc_type: 'your rewards',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_CHANGE_PLAN_SUBSCRIPTION_CTA = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'change plan',
    bloc_type: 'your rewards',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_CANCEL_SUBSCRIPTION_CTA = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'cancel',
    bloc_type: 'your rewards',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_SEE_NON_PARTICIPATING_HOTELS = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'see non participating hotels',
    bloc_type: 'participating brands',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_PREMIUM_CUSTOMER_SERVICE_BENEFITS = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'premium customer service',
    bloc_type: 'benefits',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_VOUCHER_DETAILS_TERMS_CONDITIONS = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: 'details voucher',
    bloc_interaction: 'terms and conditions',
    bloc_type: 'vouchers and discount',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_EXPIRING_DISPLAY = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'your subscription is expiring',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_PAYMENT_METHOD_CTA = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'modify',
    bloc_type: 'payment method',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_NEWSLETTER_PREF = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'manage my preferences',
    bloc_type: 'communication preference',
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_SUBSCRIPTION_PENDING_PAYMENT_DISPLAY = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: `account::${planLabel}`,
    bloc_name: planLabel,
    bloc_interaction: 'pending payment',
    bloc_type: 'display',
    context: ACCOUNT_CONTEXT
  }
})

/****** Start status gift modal ******/

export const GTM_STATUS_GIFT_MODAL_DISPLAY = ({
  type,
  isAllSignature
}: {
  type: StatusGiftType
  isAllSignature: boolean
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: isAllSignature ? 'account::signature absolute' : 'account::loyalty',
    bloc_name: 'slider gift donation',
    bloc_interaction: 'display',
    bloc_type: type.toLowerCase(),
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_STATUS_GIFT_MODAL_GIVE_NOW = ({
  type,
  isAllSignature
}: {
  type: StatusGiftType
  isAllSignature: boolean
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: isAllSignature ? 'account::signature absolute' : 'account::loyalty',
    bloc_name: 'slider gift donation',
    bloc_interaction: 'give now',
    bloc_type: type.toLowerCase(),
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_STATUS_GIFT_MODAL_FORM_CTA = ({
  type,
  isAllSignature,
  cta
}: {
  type: StatusGiftType
  isAllSignature: boolean
  cta: 'confirm' | 'cancel'
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: isAllSignature ? 'account::signature absolute' : 'account::loyalty',
    bloc_name: 'slider email verification donation',
    bloc_interaction: cta,
    bloc_type: type.toLowerCase(),
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_STATUS_GIFT_MODAL_CONFIRMATION_CLOSE = ({
  type,
  isAllSignature
}: {
  type: StatusGiftType
  isAllSignature: boolean
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: isAllSignature ? 'account::signature absolute' : 'account::loyalty',
    bloc_name: 'slider confirmation donation',
    bloc_interaction: 'close',
    bloc_type: type.toLowerCase(),
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_STATUS_GIFT_MODAL_DOUBLE_GIVE = ({
  type,
  isAllSignature
}: {
  type: StatusGiftType
  isAllSignature: boolean
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: isAllSignature ? 'account::signature absolute' : 'account::loyalty',
    bloc_name: 'slider double gift donation',
    bloc_interaction: 'double gifts',
    bloc_type: type.toLowerCase(),
    context: ACCOUNT_CONTEXT
  }
})

export const GTM_STATUS_GIFT_MODAL_DOUBLE_DISPLAY = ({
  type,
  isAllSignature
}: {
  type: StatusGiftType
  isAllSignature: boolean
}) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: isAllSignature ? 'account::signature absolute' : 'account::loyalty',
    bloc_name: 'slider double gift donation',
    bloc_interaction: 'display',
    bloc_type: type.toLowerCase(),
    context: ACCOUNT_CONTEXT
  }
})

/****** End status gift modal *******/

export const GTM_PAGE_HUB_SUBSCRIPTION = () => ({
  pageName: `account::hub subscription`,
  sub_category_level1: 'account',
  sub_category_level2: 'subscription'
})

export const GTM_PAGE_CARD_SUBSCRIPTION = ({
  planLabel
}: {
  planLabel: SouscriptionAndPlanLabel
}) => ({
  pageName: `account::${planLabel}`,
  sub_category_level1: 'account',
  sub_category_level2: planLabel
})
export const GTM_NOT_SUPPORTED_PHONE = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'phone not supported'
  }
})

export const GTM_INCORRECT_PHONE_FORMAT = ({ pageName }: { pageName: string }) => ({
  event: 'GA4event',
  eventName: 'bloc_interact',
  event_data: {
    pagename: pageName,
    form_action: 'error',
    error_type: 'technical error',
    error_field: 'incorrect phone format'
  }
})
